import yoda from '@kibt/bridge';
import { isInApp } from './device-info';
import { radar } from './log';

// 商详页数据预存维护指南
// https://docs.corp.kuaishou.com/d/home/fcAC4dOht7ammJo2s3uuVdcxs?channel=kim-cloud

export interface PrestoreDetailInfo {
    // 商品标题
    title: string;
    // 商品ID
    itemId: number | string;
    // 商品图片
    imageUrls: string[];
    // 到手价
    displaySalePrice: string;
}

export const prestoreDetail = async (d: PrestoreDetailInfo[]) => {
    const data = d.map((item: any) => ({
        title: item.title,
        itemId: item.itemId,
        imageUrls: (item.templateImageUrls || item.imageUrls).slice(0, 1),
        displaySalePrice: item.displaySalePrice,
    }));
    const prestoreDataStr = (await yoda.component.getWebConfig({ key: 'detailPrestore' }))?.message || '[]';

    let prestoreData;
    try {
        prestoreData = JSON.parse(prestoreDataStr);
    } catch (e) {
        prestoreData = [];
    }

    // 把prestoreData里面的和此次存入的data中重复的数据过滤掉, '1234' == 1234 期望返回true
    prestoreData = prestoreData.filter(item => data.findIndex(element => +element.itemId === +item.itemId) === -1);
    // 最多存储300条商品信息 LRU策略
    prestoreData = data.concat(prestoreData).slice(0, 300);
    yoda.component.setWebConfig({
        key: 'detailPrestore',
        value: JSON.stringify(prestoreData),
    });
};

export const getDetailPrestoreData = async (itemId: number | string) => {
    return yoda.component.getWebConfig({ key: 'detailPrestore' }).then(res => {
        const prestoreDataStr = res?.message || '[]';
        let prestoreData;
        try {
            prestoreData = JSON.parse(prestoreDataStr);
        } catch (e) {
            prestoreData = [];
        }
        // '1234' === 1234 期望返回true
        return prestoreData.find(item => +item.itemId === +itemId);
    });
};

// /rest/o/ecom/customer/item/info 获取商品信息
const restOEcomCustomerItemInfo = res => {
    const { data } = res;
    if (!data?.itemId) return [];
    let prestoreData = [];
    try {
        prestoreData = data?.recoItems.map(recoItem => {
            return {
                title: recoItem.title,
                itemId: recoItem.itemId,
                imageUrls: [recoItem.templateImageUrl || recoItem.imageUrl],
                displaySalePrice: recoItem.displaySalePrice,
            };
        });
    } catch (e) {}
    return [
        {
            title: data.title,
            itemId: data.itemId,
            imageUrls: data.templateImageUrls || data.imageUrls,
            displaySalePrice: data.displaySalePrice,
        },
        ...prestoreData,
    ];
};

const restOEcommerceCustomerGetActiveCartItemsV2 = res => {
    let prestoreData = [];
    try {
        const { data } = res;
        const cartItems = data?.cartItems;
        if (!cartItems?.length) return [];
        prestoreData = cartItems?.map(cartItem => {
            return {
                title: cartItem?.name,
                itemId: cartItem?.commodityId,
                imageUrls: [cartItem?.coverUrl],
                displaySalePrice: cartItem.displaySalePrice,
            };
        });
    } catch (e) {}
    return prestoreData;
};

const restOEcommerceCustomerShopCartGetShopCartInfo = res => {
    const prestoreData = [];
    try {
        const { data } = res;
        data?.pageCartInfo?.cartShopList?.forEach(cartShop => {
            cartShop?.cartItemList?.forEach(cartItem => {
                prestoreData.push({
                    title: cartItem?.title,
                    itemId: cartItem?.itemId,
                    imageUrls: [cartItem?.itemImageUrl],
                });
            });
        });
        Object.values(data?.recommend?.recommendItem || {})?.forEach(recommendItem => {
            (recommendItem as any)?.recommendItemList?.forEach(cartItem => {
                prestoreData.push({
                    title: cartItem?.title,
                    itemId: cartItem?.itemId,
                    imageUrls: [cartItem?.itemImageUrl],
                });
            });
        });
    } catch (e) {}
    return prestoreData;
};

const restOEcomCustomerItemRecoRecommendLike = res => {
    let prestoreData = [];
    try {
        const { data } = res;
        prestoreData = data.recoItems.map(recoItem => {
            return {
                title: recoItem.title,
                itemId: recoItem.itemId,
                imageUrls: recoItem.templateImageUrls || recoItem.imageUrls,
                displaySalePrice: recoItem.displaySalePrice,
            };
        });
    } catch (e) {}
    return prestoreData;
};

const restOEcomProductCustomerItemPlcMain = res => {
    const { data } = res;
    if (!data?.itemId) return [];
    return [
        {
            title: data.title,
            itemId: data.itemId,
            imageUrls: data.imageUrls,
            displaySalePrice: data.displaySalePrice,
        },
    ];
};

const restOEcomCustomerItemRecoYellowCartDouble = res => {
    const { data } = res;
    if (!data?.length) return [];
    let prestoreData = [] as PrestoreDetailInfo[];
    try {
        prestoreData = data.map(recoItem => {
            return {
                title: recoItem.title,
                itemId: recoItem.itemId,
                imageUrls: recoItem.imageUrls,
                displaySalePrice: recoItem.displaySalePrice,
            };
        });
    } catch (e) {}
    return prestoreData;
};

const restOEcomCustomerShopProfile = res => {
    const { data } = res;
    if (!data?.itemPage?.items?.length) return [];
    let prestoreData = [] as PrestoreDetailInfo[];
    try {
        prestoreData = data?.itemPage?.items?.map(recoItem => {
            return {
                title: recoItem.title,
                itemId: recoItem.itemId,
                imageUrls: [recoItem.itemImageUrl],
                displaySalePrice: recoItem.displaySalePrice,
            };
        });
    } catch (e) {}
    return prestoreData;
};

const restOEcomProductCustomerItemRecoMakeupInfo = res => {
    const { data } = res;
    const { recoItems, shopRecoItems } = data;
    if (!recoItems?.length && !shopRecoItems?.length) return [];
    let prestoreData = [] as PrestoreDetailInfo[];
    try {
        prestoreData = recoItems.concat(shopRecoItems).map(recoItem => {
            return {
                title: recoItem.title,
                itemId: recoItem.itemId,
                imageUrls: recoItem.templateImageUrls || recoItem.imageUrls,
                displaySalePrice: recoItem?.salePrice?.displayAmount,
            };
        });
    } catch (e) {}
    return prestoreData;
};

const restOEcommerceMarketingCCustomerKwaiShopFlashSaleQueryByTurn = res => {
    const { data } = res || {};
    const productDetailViewList = data?.productDetailViewList || [];
    if (!productDetailViewList.length) return [];
    let prestoreData = [] as PrestoreDetailInfo[];
    try {
        prestoreData = productDetailViewList.map(product => {
            return {
                title: product.name,
                itemId: product.itemId,
                imageUrls: [product.templateImageUrl || product.coverUrl],
                displaySalePrice: product?.displaySalePrice,
            };
        });
    } catch (e) {}
    return prestoreData;
};

const restOEcommerceMarketingCCustomerKwaiShopNewProductSearch = res => {
    const { data } = res || {};
    const list = data?.list || [];
    if (!list.length) return [];
    let prestoreData = [] as PrestoreDetailInfo[];
    try {
        prestoreData = list.map(product => {
            return {
                title: product.name,
                itemId: product.itemId,
                imageUrls: [product.templateImageUrl || product.coverUrl],
                displaySalePrice: product?.displaySalePrice,
            };
        });
    } catch (e) {}
    return prestoreData;
};

const restOEcomProductCustomerFootMarkQuery = res => {
    const { data } = res || {};
    if (!data.length) return [];
    let prestoreData = [] as PrestoreDetailInfo[];
    try {
        prestoreData = data.map(log => {
            const item = log?.item || {};
            return {
                title: item.title,
                itemId: item.bizId,
                imageUrls: [item.itemImageUrl],
                displaySalePrice: item?.displaySalePrice,
            };
        });
    } catch (e) {}
    return prestoreData;
};

const restOEcommerceCustomerShopCartStoreCartItem = res => {
    const cartShopList = res?.data?.cartShopList || [];
    if (!cartShopList.length) return [];
    const prestoreData = [] as PrestoreDetailInfo[];
    try {
        cartShopList.map(cartShop => {
            const cartItemList = cartShop?.cartItemList || [];
            cartItemList.map(cartItem => {
                prestoreData.push({
                    title: cartItem?.title,
                    itemId: cartItem?.itemId,
                    imageUrls: [cartItem?.itemImageUrl],
                    displaySalePrice: cartItem?.displaySalePrice,
                });
            });
        });
    } catch (e) {}
    return prestoreData;
};

const restOEcomCustomerShopHome = res => {
    const items = res?.data?.itemPage?.items || [];
    if (!items.length) return [];
    let prestoreData = [] as PrestoreDetailInfo[];
    try {
        prestoreData = items.map(item => {
            return {
                title: item?.title,
                itemId: item?.itemId,
                imageUrls: [item?.itemImageUrl],
                displaySalePrice: item?.displaySalePrice,
            };
        });
    } catch (e) {}
    return prestoreData;
};

const restOEcomCustomerShopItemList = res => {
    const items = res?.data?.items || [];
    if (!items.length) return [];
    let prestoreData = [] as PrestoreDetailInfo[];
    try {
        prestoreData = items.map(item => {
            return {
                title: item?.title,
                itemId: item?.itemId,
                imageUrls: [item?.itemImageUrl],
                displaySalePrice: item?.displaySalePrice,
            };
        });
    } catch (e) {}
    return prestoreData;
};

export const ApiResToDetailPrestoreMap: {
    [key: string]: (any) => PrestoreDetailInfo[];
} = {
    '/rest/o/ecom/customer/item/info': restOEcomCustomerItemInfo,
    '/rest/o/ecommerce/customer/getActiveCartItemsV2': restOEcommerceCustomerGetActiveCartItemsV2,
    '/rest/o/ecommerce/customer/shopCart/getShopCartInfo': restOEcommerceCustomerShopCartGetShopCartInfo,
    '/rest/o/ecom/customer/item/reco/recommendLike': restOEcomCustomerItemRecoRecommendLike,
    '/rest/o/ecom/product/customer/item/plc/main': restOEcomProductCustomerItemPlcMain,
    '/rest/o/ecom/customer/item/reco/yellowCartDouble': restOEcomCustomerItemRecoYellowCartDouble,
    '/rest/o/ecom/customer/shop/profile': restOEcomCustomerShopProfile,
    '/rest/o/ecom/product/customer/item/reco/makeup/info': restOEcomProductCustomerItemRecoMakeupInfo,
    '/rest/o/ecommerce/marketing/c/customer/kwai/shop/flashSale/queryByTurn': restOEcommerceMarketingCCustomerKwaiShopFlashSaleQueryByTurn,
    '/rest/o/ecommerce/marketing/c/customer/kwai/shop/new/product/search': restOEcommerceMarketingCCustomerKwaiShopNewProductSearch,
    '/rest/o/ecom/product/customer/footMark/query': restOEcomProductCustomerFootMarkQuery,
    '/rest/o/ecommerce/customer/shopCart/storeCartItem': restOEcommerceCustomerShopCartStoreCartItem,
    '/rest/o/ecom/customer/shop/home': restOEcomCustomerShopHome,
    '/rest/o/ecom/customer/shop/item/list': restOEcomCustomerShopItemList,
};

// 不需要做商详页数据预存的api
export const IgnoredApi = [
    '/rest/o/ecommerce/privacy/encrypt/customer/address/queryByUserId',
    '/rest/o/w/kconf/loggedOut',
    '/rest/o/ecommerce/marketing/c/customer/coupon/item/list',
    '/rest/o/ecommerce/marketing/c/customer/coupon/claim',
];

export const setPrestoreDataFromRes = (params: { res: any; api: string }) => {
    const { res, api } = params;
    if (!isInApp) return;
    const _api = api.split('?')[0];
    // 是否忽略该api返回
    if (IgnoredApi.includes(_api)) return;
    if (!Object.prototype.hasOwnProperty.call(ApiResToDetailPrestoreMap, _api)) {
        const strRes = JSON.stringify(res);
        const searchTags = ['itemId', 'title', 'salePrice', 'marketPrice'];
        // 如果res中存在searchTags的字段，说明该请求数据可能可以清洗后预存
        // 如果不需要预存该api数据，请将该api添加到 IgnoredApi 中
        const canBePrestored = searchTags.every(searchTag => strRes.includes(searchTag));
        if (canBePrestored) {
            radar.event({
                name: 'UnhandledPrestoreApi',
                params: {
                    api: _api,
                },
            });
        }
        return;
    }
    const getDetalPrestoreData = ApiResToDetailPrestoreMap[_api];
    const data = getDetalPrestoreData(res);
    prestoreDetail(data);
};
